/**
 * Local preferences.
 *
 * Everything here is a property of *this browser*, not of the account or the
 * Gateway: which theme this screen shows, what Enter does on this keyboard.
 * They never travel over the wire, so they live in localStorage next to the
 * connection settings rather than in any protocol call.
 *
 * Each one keeps an explicit "system" / "auto" value instead of resolving to a
 * boolean at write time. A device that follows the system theme should keep
 * following it when the system changes, not freeze at whatever it was the day
 * the switch was flipped.
 */

import { LANGUAGES, type Language } from "./i18n";

const STORAGE_KEY = "cllw:preferences:v1";

export type ThemePreference = "system" | "light" | "dark";
/** `auto` keeps the old rule: Enter sends on a keyboard, breaks a line on touch. */
export type EnterPreference = "auto" | "send" | "newline";

export type Preferences = {
  theme: ThemePreference;
  enter: EnterPreference;
  /** English unless the reader asks otherwise; the browser locale does not decide. */
  language: Language;
};

export const DEFAULT_PREFERENCES: Preferences = { theme: "system", enter: "auto", language: "en" };

const THEMES: ThemePreference[] = ["system", "light", "dark"];
const ENTERS: EnterPreference[] = ["auto", "send", "newline"];

export function loadPreferences(): Preferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return DEFAULT_PREFERENCES;
    }
    const parsed = JSON.parse(raw) as Partial<Preferences>;
    return {
      theme: THEMES.includes(parsed.theme as ThemePreference)
        ? (parsed.theme as ThemePreference)
        : DEFAULT_PREFERENCES.theme,
      enter: ENTERS.includes(parsed.enter as EnterPreference)
        ? (parsed.enter as EnterPreference)
        : DEFAULT_PREFERENCES.enter,
      language: LANGUAGES.includes(parsed.language as Language)
        ? (parsed.language as Language)
        : DEFAULT_PREFERENCES.language,
    };
  } catch {
    // Private mode, disabled storage, corrupted JSON: defaults are always fine.
    return DEFAULT_PREFERENCES;
  }
}

export function savePreferences(preferences: Preferences): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    // Not being able to remember a preference is not worth an error.
  }
}

/**
 * Puts the chosen theme on the root element.
 *
 * `system` removes the attribute entirely rather than writing a resolved value,
 * which is what lets the media query in the stylesheet stay in charge.
 */
export function applyTheme(theme: ThemePreference): void {
  const root = document.documentElement;
  if (theme === "system") {
    delete root.dataset.theme;
  } else {
    root.dataset.theme = theme;
  }
}

/** True when Enter should send rather than insert a newline. */
export function enterSends(preference: EnterPreference): boolean {
  if (preference === "send") {
    return true;
  }
  if (preference === "newline") {
    return false;
  }
  // A phone keyboard has no comfortable Shift+Enter, so touch breaks the line.
  return !window.matchMedia?.("(pointer: coarse)").matches;
}
